import { useCallback, useEffect, useState } from 'react';
import type { Node } from '@xyflow/react';

interface NodeData extends Record<string, unknown> {
  label: string;
  description?: string;
  type: 'start' | 'ai' | 'condition' | 'api' | 'end';
}

interface NodePropertiesPanelProps {
  selectedNode: Node<NodeData> | null;
  onNodeDataChange: (nodeId: string, data: Partial<NodeData>) => void;
}

export default function NodePropertiesPanel({
  selectedNode,
  onNodeDataChange,
}: NodePropertiesPanelProps) {
  const [localData, setLocalData] = useState<Partial<NodeData>>({});

  useEffect(() => {
    if (selectedNode) {
      setLocalData(selectedNode.data);
    } else {
      setLocalData({});
    }
  }, [selectedNode]);

  const handleChange = useCallback(
    (key: string, value: unknown) => {
      const newData = { ...localData, [key]: value };
      setLocalData(newData);
      if (selectedNode) {
        onNodeDataChange(selectedNode.id, { [key]: value });
      }
    },
    [localData, selectedNode, onNodeDataChange]
  );

  const handleJsonChange = useCallback(
    (key: string, value: string) => {
      try {
        const parsed = JSON.parse(value);
        handleChange(key, parsed);
      } catch {
        // Invalid JSON, don't update
      }
    },
    [handleChange]
  );

  if (!selectedNode) {
    return (
      <div
        style={{
          width: '300px',
          height: '100%',
          background: '#f9fafb',
          borderLeft: '1px solid #e5e7eb',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        <span style={{ fontSize: '48px', marginBottom: '16px' }}>📋</span>
        <p style={{ fontSize: '14px', textAlign: 'center' }}>
          选择一个节点查看和编辑属性
        </p>
      </div>
    );
  }

  const nodeType = selectedNode.data.type;
  const typeLabels: Record<string, string> = {
    start: '开始节点',
    ai: 'AI节点',
    condition: '条件节点',
    api: 'API节点',
    end: '结束节点',
  };

  const typeColors: Record<string, string> = {
    start: '#22c55e',
    ai: '#a855f7',
    condition: '#eab308',
    api: '#3b82f6',
    end: '#ef4444',
  };

  const renderField = (
    label: string,
    key: string,
    type: 'text' | 'textarea' | 'number' | 'select' | 'json' | 'code',
    options?: string[],
    placeholder?: string
  ) => {
    const value = localData[key];

    return (
      <div key={key} style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </label>
        {type === 'textarea' ? (
          <textarea
            value={(value as string) || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={placeholder}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
          />
        ) : type === 'select' ? (
          <select
            value={(value as string) || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              background: 'white',
              outline: 'none',
            }}
          >
            {options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : type === 'json' ? (
          <textarea
            value={value ? JSON.stringify(value, null, 2) : ''}
            onChange={(e) => handleJsonChange(key, e.target.value)}
            placeholder={placeholder || '{\n  "key": "value"\n}'}
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'monospace',
              resize: 'vertical',
              outline: 'none',
            }}
          />
        ) : type === 'number' ? (
          <input
            type="number"
            value={(value as number) || 0}
            onChange={(e) => handleChange(key, parseInt(e.target.value, 10) || 0)}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        ) : (
          <input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        )}
      </div>
    );
  };

  const renderNodeSpecificFields = () => {
    switch (nodeType) {
      case 'start':
        return (
          <>
            {renderField('初始变量', 'initialVariables', 'json', undefined, '{\n  "variableName": "value"\n}')}
          </>
        );

      case 'ai':
        return (
          <>
            {renderField('Prompt', 'prompt', 'textarea', undefined, '输入AI提示词...')}
            {renderField('系统提示词', 'systemPrompt', 'textarea', undefined, '输入系统提示词...')}
            {renderField('模型', 'model', 'select', ['gpt-4', 'gpt-3.5-turbo', 'claude-3', 'gemini-pro'])}
            {renderField('Temperature', 'temperature', 'number')}
            {renderField('Max Tokens', 'maxTokens', 'number')}
            {renderField('输出变量', 'outputVariable', 'text', undefined, 'result')}
          </>
        );

      case 'condition':
        return (
          <>
            {renderField('条件表达式', 'condition', 'text', undefined, '例如: input.value > 10')}
            {renderField('真分支标签', 'trueLabel', 'text', undefined, '是')}
            {renderField('假分支标签', 'falseLabel', 'text', undefined, '否')}
          </>
        );

      case 'api':
        return (
          <>
            {renderField('请求方法', 'method', 'select', ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'])}
            {renderField('URL', 'url', 'text', undefined, 'https://api.example.com/data')}
            {renderField('请求头', 'headers', 'json', undefined, '{\n  "Content-Type": "application/json"\n}')}
            {renderField('请求体', 'body', 'textarea', undefined, '{\n  "key": "value"\n}')}
            {renderField('超时(ms)', 'timeout', 'number')}
            {renderField('重试次数', 'retryCount', 'number')}
            {renderField('输出变量', 'outputVariable', 'text', undefined, 'response')}
          </>
        );

      case 'end':
        return (
          <>
            {renderField('输出变量', 'outputVariables', 'json', undefined, '{\n  "result": "{{variable}}"\n}')}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        width: '300px',
        height: '100%',
        background: '#f9fafb',
        borderLeft: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid #e5e7eb',
          background: 'white',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
          }}
        >
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: typeColors[nodeType],
            }}
          />
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {typeLabels[nodeType]}
          </span>
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#111827',
          }}
        >
          节点属性
        </h3>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
        }}
      >
        {renderField('标签', 'label', 'text', undefined, '节点名称')}
        {renderField('描述', 'description', 'textarea', undefined, '节点描述...')}
        
        <div
          style={{
            height: '1px',
            background: '#e5e7eb',
            margin: '16px 0',
          }}
        />
        
        {renderNodeSpecificFields()}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid #e5e7eb',
          background: 'white',
          fontSize: '11px',
          color: '#6b7280',
        }}
      >
        节点 ID: {selectedNode.id}
      </div>
    </div>
  );
}
